import React, { useState } from 'react'
import { FEATURE_GROUPS, PLANS } from '../lib/types/pricing-table.config'
import { Button } from './Button'
import { IconCircleX, IconChevronDown, IconCheck } from '@tabler/icons-react'
import { PlanId } from 'util/prices'
import { ModalV2 } from './Common/ModalV2'
import { PricingCadenceSelector } from './PricingCadenceSelector'
import { useMediaQuery } from '@uidotdev/usehooks'
import { HelperIcon } from './HelperIcon'
import { classNames } from 'util/classNames'
import { MobilePricingTable } from './MobilePricingTable'

export type PricingTableShow = 'all' | 'heading' | 'comparison'

type PricingTableProps = {
  condensed?: boolean
  currentPlan?: PlanId | null
  onManageBilling?: () => void
  onSelectPlan?: ({ id, name }: { id: string; name: string }) => void
  isLoadingBillingSession?: boolean
  show?: PricingTableShow
  href?: string
  isTrialing?: boolean
  setShowUpgradeTrialModal?: (show: boolean) => void
}

export const PricingTable = ({
  condensed = false,
  currentPlan = null,
  onManageBilling = () => {},
  isLoadingBillingSession = false,
  show = 'all',
  href = '',
  onSelectPlan,
  isTrialing = false,
  setShowUpgradeTrialModal = () => {},
}: PricingTableProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [showModal, setShowModal] = useState(false)

  if (isMobile) {
    return (
      <MobilePricingTable
        condensed={condensed}
        currentPlan={currentPlan}
        onManageBilling={onManageBilling}
        isLoadingBillingSession={isLoadingBillingSession}
        show={show}
        href={href}
        onSelectPlan={onSelectPlan}
        isTrialing={isTrialing}
        setShowUpgradeTrialModal={setShowUpgradeTrialModal}
      />
    )
  }

  return (
    <>
      <PricingTableInner
        condensed={condensed}
        currentPlan={currentPlan}
        onManageBilling={onManageBilling}
        isLoadingBillingSession={isLoadingBillingSession}
        show={show}
        href={href}
        onSelectPlan={onSelectPlan}
        isTrialing={isTrialing}
        setShowUpgradeTrialModal={setShowUpgradeTrialModal}
        setShowModal={setShowModal}
      />
      <ModalV2
        className="md:max-w-5xl"
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <PricingTableInner
          condensed={false}
          currentPlan={currentPlan}
          onManageBilling={onManageBilling}
          isLoadingBillingSession={isLoadingBillingSession}
          show="all"
          href={href}
          onSelectPlan={onSelectPlan}
          isTrialing={isTrialing}
          setShowUpgradeTrialModal={setShowUpgradeTrialModal}
          setShowModal={setShowModal}
        />
      </ModalV2>
    </>
  )
}

const PricingTableInner = ({
  condensed = false,
  currentPlan = null,
  setShowModal = () => {},
  onManageBilling = () => {},
  isLoadingBillingSession = false,
  show = 'all',
  href = '',
  onSelectPlan,
  isTrialing = false,
  setShowUpgradeTrialModal = () => {},
}: PricingTableProps & {
  setShowModal?: (show: boolean) => void
  onManageBilling?: () => void
  isLoadingBillingSession?: boolean
  show?: PricingTableShow
  href?: string
  onSelectPlan?: ({ id, name }: { id: string; name: string }) => void
  isTrialing?: boolean
  setShowUpgradeTrialModal?: (show: boolean) => void
}) => {
  const [billingCycle, setBillingCycle] = useState<'Monthly' | 'Annual'>(
    currentPlan?.includes('Yearly')
      ? 'Annual'
      : !!currentPlan
      ? 'Monthly'
      : 'Monthly'
  )

  const [isLoadingPlanSelect, setIsLoadingPlanSelect] = useState<PlanId | null>(
    null
  )

  const handleSelectPlan = (plan: { id: PlanId; name: string }) => {
    if (onSelectPlan) {
      onSelectPlan(plan)
      return
    }
    setIsLoadingPlanSelect(plan.id)
    if (currentPlan === plan.id && onManageBilling) {
      onManageBilling()
    } else {
      const link = `${
        process.env.NEXT_PUBLIC_BASE_URL
      }/purchase?plan=${encodeURIComponent(
        plan.id
      )}&replaceHistoryPage=${encodeURIComponent(window.location.pathname)}`
      window.open(link, '_self')
    }
    setTimeout(() => {
      setIsLoadingPlanSelect(null)
    }, 4000)
  }

  const filteredPlans = PLANS.filter(
    (plan) => plan.billingCycle === billingCycle
  )

  return (
    <div className={`${'mx-auto'}`}>
      <div className="mb-2 flex flex-col items-end gap-1">
        <PricingCadenceSelector
          billingCycle={billingCycle}
          setBillingCycle={setBillingCycle}
        />
      </div>

      <div className="w-full overflow-x-auto">
        <table className="min-w-full table-auto border-separate">
          {(show == 'all' || show == 'heading') && (
            <thead className="bg-slate-100">
              <tr>
                {!condensed && (
                  <th className="w-1/4 border-b border-slate-200 bg-white px-4"></th>
                )}
                {filteredPlans.map((plan, idx) => (
                  <th
                    key={idx}
                    className={classNames(
                      'relative min-w-[250px] rounded-t-md px-4 text-left',
                      !condensed ? 'w-1/4' : 'w-1/3'
                    )}
                  >
                    {currentPlan === plan.id && (
                      <Checkbox name="plan" value={plan.id} checked={true} />
                    )}
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-gray-700">
                        {plan.name}
                      </h3>

                      {condensed ? (
                        <p className="text-xs text-gray-500">
                          {plan.condensedDescription}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500">
                          {plan.description}
                        </p>
                      )}
                      <div className="mt-4 flex items-center gap-2">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            {plan.originalPrice && (
                              <span className="text-sm text-gray-400 line-through">
                                {plan.originalPrice}
                              </span>
                            )}
                            <p className="text-2xl font-semibold text-green-600">
                              {plan.price}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <p className="whitespace-nowrap text-xs text-gray-500">
                              / per month
                            </p>
                          </div>
                          <p className="text-[10px] text-gray-500">
                            {plan.billingCycle === 'Annual'
                              ? `billed annually`
                              : ``}
                          </p>
                        </div>
                      </div>
                      <Button
                        className={`mt-2 !h-8 w-full text-white ${
                          currentPlan === plan.id
                            ? '!bg-toasty-blue-600'
                            : '!bg-slate-800'
                        }`}
                        onClick={() =>
                          isTrialing && currentPlan === plan.id
                            ? setShowUpgradeTrialModal(true)
                            : handleSelectPlan({ id: plan.id, name: plan.name })
                        }
                        loading={
                          (isLoadingBillingSession &&
                            currentPlan === plan.id) ||
                          (isLoadingPlanSelect === plan.id &&
                            isLoadingPlanSelect !== null)
                        }
                      >
                        {isTrialing && currentPlan === plan.id
                          ? 'Upgrade Now'
                          : currentPlan === plan.id
                          ? 'Manage Billing'
                          : 'Select plan'}
                      </Button>
                    </div>
                  </th>
                ))}
              </tr>
              {condensed && (
                <tr
                  className="cursor-pointer bg-slate-100"
                  onClick={(e) => {
                    if (href) {
                      e.preventDefault()
                    } else {
                      setShowModal(true)
                    }
                  }}
                >
                  {!condensed && <td className="bg-white"></td>}
                  <td className="w-full" colSpan={filteredPlans.length}>
                    <a
                      href={href || undefined}
                      className="flex w-full items-center justify-center px-4 py-1 text-center text-xs font-bold text-slate-500 hover:underline"
                    >
                      Compare plans{' '}
                      <IconChevronDown className="inline h-4 w-4 text-slate-500" />
                    </a>
                  </td>
                </tr>
              )}
            </thead>
          )}
          {show === 'comparison' && (
            <thead className="">
              <tr>
                <th className="bg-white px-4"></th>
                {filteredPlans.map((plan, idx) => (
                  <th key={idx} className="w-1/3 bg-slate-100 px-4">
                    {plan.name}
                  </th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {!condensed &&
              (show == 'comparison' || show == 'all') &&
              FEATURE_GROUPS.map((group, idx) => (
                <React.Fragment key={idx}>
                  <tr className="divide-y divide-slate-100">
                    {group.groupName.trim() !== '' ? (
                      <td
                        colSpan={filteredPlans.length + 1}
                        className="bg-slate-100 px-4 py-2 text-left text-sm font-semibold text-gray-600"
                      >
                        {group.groupName}
                      </td>
                    ) : null}
                  </tr>
                  {group.features.map((feature, index) => (
                    <tr key={index} className="divide-y divide-slate-100">
                      <td className="overflow-visible whitespace-nowrap px-4 py-3 text-left text-sm text-gray-700">
                        <span className="truncate">{feature.name}</span>{' '}
                        {feature.tooltip && (
                          <HelperIcon
                            className="ml-0.5 mb-1 h-4 w-4"
                            id={feature.tooltip}
                            helper={feature.tooltip}
                          />
                        )}
                      </td>
                      {filteredPlans.map((plan, idx) => {
                        const value = feature.values[plan.id]
                        return (
                          <td key={idx} className="px-4 py-3 text-center">
                            {typeof value === 'boolean' ? (
                              value ? (
                                <IconCheck className="mx-auto h-5 w-5 text-green-500" />
                              ) : (
                                <IconCircleX className="mx-auto h-5 w-5 text-slate-400" />
                              )
                            ) : (
                              <span className="text-gray-600">{value}</span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            {!condensed && (
              <tr>
                <td></td>
                {filteredPlans.map((plan, idx) => (
                  <td key={idx} className="px-4 text-center">
                    <Button
                      className={`mt-2 !h-8 w-full text-white ${
                        currentPlan === plan.id
                          ? '!bg-toasty-blue-600'
                          : '!bg-slate-800'
                      }`}
                      onClick={() =>
                        isTrialing && currentPlan === plan.id
                          ? setShowUpgradeTrialModal(true)
                          : handleSelectPlan({ id: plan.id, name: plan.name })
                      }
                      loading={
                        (isLoadingBillingSession && currentPlan === plan.id) ||
                        (isLoadingPlanSelect === plan.id &&
                          isLoadingPlanSelect !== null)
                      }
                    >
                      {isTrialing && currentPlan === plan.id
                        ? 'Upgrade Now'
                        : currentPlan === plan.id
                        ? 'Manage Billing'
                        : 'Select plan'}
                    </Button>
                  </td>
                ))}
              </tr>
            )}
            {condensed &&
              FEATURE_GROUPS.filter(
                (group) => group.groupName.trim() === ''
              ).map((group, idx) => (
                <tr key={idx}>
                  <td colSpan={filteredPlans.length + 1}></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export const Checkbox = ({
  name,
  value,
  checked,
}: {
  name: string
  value: string
  checked: boolean
}) => {
  return (
    <input
      id={name}
      name={name}
      type="checkbox"
      value={value}
      checked={checked}
      readOnly
      aria-describedby={`${name}-description`}
      className="absolute right-2 top-2 h-4 w-4 rounded-full border-gray-300 text-toasty-blue-600 focus:ring-toasty-blue-600"
    />
  )
}
